import { useId, useState, type MouseEvent } from "react";
import { Menu, MenuItem } from "@mui/material";
import Typography from "@mui/material/Typography";
import { matchQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { translationKeys, translationsQueryOptions, useTranslation } from "@privacy/translations";
import { queryClient } from "../api/queryClient";
import { userInfoKeys } from "../api/userInfo";
import { useCurrentOrganisation } from "../root/CurrentOrganisationProvider";
import { SidebarItem } from "../root/layout/SidebarMenuItem";
import { ChevronsUpDownIcon, type IconProps } from "../shared/icons";
import { accessibleOrganisations, type EnterpriseCustomer } from "./enterpriseCustomer";

type EnterpriseCustomerOrganisationPickerProps = { customer: EnterpriseCustomer };

export const EnterpriseCustomerOrganisationPicker = ({ customer }: EnterpriseCustomerOrganisationPickerProps) => {
  const currentOrganisation = useCurrentOrganisation();
  const { language } = useTranslation();
  const navigate = useNavigate();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const organisations = accessibleOrganisations(customer, language);
  const canSwitch = organisations.length > 1;

  return (
    <>
      <SidebarItem
        {...(canSwitch && {
          "component": "button",
          "onClick": (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
          "aria-controls": open ? menuId : undefined,
          "aria-haspopup": "true",
          "aria-expanded": open ? "true" : undefined,
          "renderEndIcon": (props: IconProps) => <ChevronsUpDownIcon {...props} />,
        })}
        isInteractive={canSwitch}
        label={currentOrganisation.name}
        truncateLabel
        labelSx={{
          fontSize: theme => theme.typography.pxToRem(16),
          fontWeight: 700,
          lineHeight: 1.2,
          color: theme => theme.palette.text.primary,
        }}
        sx={{ mt: 0, mb: 2 }}
      />

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        disableScrollLock
        slotProps={{
          paper: { onMouseLeave: () => setAnchorEl(null), sx: { width: "250px", maxHeight: "60vh" } },
        }}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {organisations.map(organisation => (
          <MenuItem
            key={organisation.id}
            selected={organisation.id === currentOrganisation.id}
            onMouseEnter={() => void queryClient.prefetchQuery(translationsQueryOptions(organisation.lang))}
            onClick={() => {
              setAnchorEl(null);
              if (organisation.id === currentOrganisation.id) return;
              queryClient.removeQueries({
                predicate: query =>
                  ![userInfoKeys.user(), translationKeys.all()].some(queryKey => matchQuery({ queryKey }, query)),
              });
              navigate(`/${organisation.slug}`);
            }}
          >
            <Typography
              sx={{
                fontSize: theme => theme.typography.pxToRem(14),
                fontWeight: organisation.id === customer.id ? 600 : undefined,
              }}
            >
              {organisation.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
